import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import useAnchoredPortalPanel from '../../hooks/useAnchoredPortalPanel';
import { SHEET_Z, SHEET_BOTTOM } from '../../constants/overlayLayers';

/**
 * THE disclosure/popover primitive. Trigger button + anchored panel.
 *
 * Was `components/gags/GagDropdown.js` until 2026-08-08. Nothing about it was
 * ever gag-specific — the name was the only gag thing about it, and it had
 * already been reused three times outside the breakdown (`TeamManage.js`,
 * `MyTeamTasks.js`, `BudgetCostCodes.js`) before the rename. The old path is a
 * deprecated re-export shim; import from here.
 *
 * Everything it does, because callers depend on ALL of it — do NOT simplify the
 * API down to the cases today's callers happen to use:
 *  - open/close mechanics shared rather than re-implemented per control
 *    (the pattern GagSavedViews established: outside-click + Escape close)
 *  - a `count` prop rendering a "(N)" badge on the trigger when > 0
 *  - a chevron that rotates with the open state
 *  - focus returns to the trigger on Escape/close, so keyboard users are not
 *    dropped to <body>
 *  - real `aria-expanded` / `aria-haspopup` (`'menu'` or `'dialog'`; a dialog
 *    panel also gets `role="dialog"`)
 *  - focus-visible rings come from the BTN trigger classes
 *
 * 🔑 The panel is `z-50` and that is part of the toolbar's z-index contract —
 * it opens INSIDE the `z-20` stacking context of `components/ui/Toolbar.js`,
 * which is what puts it above the sticky `z-10` group headers. Read Toolbar's
 * docblock before changing either number.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 `portal` — OPT-IN, 2026-08-09. FOR A TRIGGER INSIDE A CLIPPING ANCESTOR.
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 **THE DEFECT THIS EXISTS FOR WAS A CLIP, NOT A LAYERING PROBLEM, AND THE
 * DIFFERENCE IS THE WHOLE POINT — `z-index` CANNOT FIX IT AT ANY VALUE.**
 * Reported by the product owner, 2026-08-09: *"scene dropdown is hidden BEHIND
 * the expanded card. the other drop downs work"*. It looks exactly like a
 * stacking bug and is not one.
 *
 * Measured in headless Chromium against the compiled stylesheet, on the
 * Breakdown's inline Scenes cell (`GagSceneMultiSelect`):
 *  - The panel's stacking-context chain from the panel to `<html>` contains
 *    **NO intervening stacking context at all** — every ancestor is
 *    `position: static`/`relative` at `z-index: auto` with `opacity: 1`. So
 *    the `z-50` resolves in the ROOT context and already outranks everything
 *    the expanded detail row paints. **The z-index was never the problem.**
 *  - The panel is CLIPPED instead, by TWO ancestors, both in its
 *    containing-block chain:
 *      1. the Scenes `<td>` itself, which carries `truncate`
 *         (= `overflow: hidden`) to ellipse the read-only `scenes_display`.
 *         Cell box 120×49; panel box 256×257 — it overflowed the cell's
 *         bottom by **250px** and its right by **144px**, leaving a **7px**
 *         sliver visible. Probes at 5%/25%/50%/75%/95% of the panel: **0/5**
 *         hit the panel; they hit the summary row's `TD` and, below it,
 *         `GagDetailPanel`'s buttons — which is precisely what reads as
 *         *"behind the expanded card"*.
 *      2. the ONE page-level scroll region (`gag-group-scroll-region`,
 *         `overflow: auto` + a `max-height`). With clip (1) lifted the panel
 *         still overflowed the region's bottom by **197px** on the last row
 *         (**1/4** probes visible).
 *  - The control that proves it: setting **only** `td.style.overflow =
 *    'visible'` — touching no z-index, no DOM order, no opacity — takes the
 *    panel from **0/5 to 5/5** visible, painted above the detail panel.
 *
 * 🔑 **Neither clip is removable.** (1) is what keeps a long scene list inside
 * a 120px column; (2) is the scroll model the sticky headers require
 * (`constants/stickyTable.js`). An element cannot escape an ancestor's
 * `overflow` clip while it remains a descendant — so the ONLY fix is to stop
 * being one. Hence a portal, which is the answer the z-index contract already
 * records: *"a kebab menu that must escape the bar portals at `z-60`"*.
 *
 * 🥇 **PRECEDENT, NOT INVENTION: `KebabMenu` in `components/gags/
 * GagGroupTable.js` already does exactly this**, on this very surface, for
 * this very clipper. This prop generalises that one so the two cannot drift:
 * same two-phase positioning (a first guess from the trigger rect, refined in
 * `useLayoutEffect` from the panel's measured box before paint), same
 * viewport clamp, same flip-above-when-it-would-overflow-the-bottom, same
 * `zIndex: 60` as an inline style, and the same **close** on scroll/resize.
 *
 * 🔴 **CLOSE ON SCROLL — DO NOT "IMPROVE" THIS INTO POSITION-TRACKING.** A
 * fixed-position panel does not move with its trigger, so a scroll would
 * visibly detach it from the row it belongs to. Tracking is more machinery
 * for a worse result: the row itself scrolls away underneath. The kebab
 * settled this and says so at its own site.
 *
 * 🪤 **The scroll listener is capture-phase on `window`, so it also fires for
 * the panel's OWN inner scrolling — which the kebab never hit because its
 * menu does not scroll, and this one's does** (`GagSceneMultiSelect` has a
 * `max-h-48 overflow-y-auto` option list). Scrolling the options would have
 * dismissed the panel under the user's finger. Events originating inside the
 * panel are therefore ignored; that test is load-bearing, not defensive.
 *
 * ⚠️ **It is OPT-IN and defaults to `false`, so every existing caller —
 * `Group ▾` / `Filters ▾` / `Views ▾` on the Breakdown and `Sort ▾` /
 * `Filters ▾` on the order pages — renders byte-for-byte what it rendered
 * before, still at `z-50`, still anchored.** Those triggers sit on a
 * `ui/Toolbar`, which clips nothing, so they never had this problem. **Do not
 * flip the default**: portalling a panel that does not need it swaps a free,
 * self-positioning anchor for JS coordinates that must be recomputed, and
 * costs the close-on-scroll behaviour those callers should not have.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 📱 BELOW `md` EVERY PANEL IS A BOTTOM SHEET — 2026-10-01, NOT OPT-IN.
 * ═══════════════════════════════════════════════════════════════════════════
 * An anchored `absolute` panel sized for a desktop (`w-[28rem]`, `w-72`)
 * WIDENED THE PAGE on a 430px phone (My Tasks › Filters and Breakdown ›
 * Filters: the page grew to 557px and Android Chrome's fixed bottom nav slid
 * off-screen), and an `align="right"` panel under a left-ish trigger was
 * CLIPPED past the left edge (Budget › Manage version / Actions). Below `md`
 * the panel therefore portals to `<body>` as a sheet: `fixed`, 8px gutters,
 * its bottom 8px above the 64px nav (`SHEET_BOTTOM`), `max-height: 70vh` with
 * its own scroll, over a dimmed backdrop, at the escape tier (`SHEET_Z`, 60 —
 * above modal overlays, so a dropdown inside a dialog still wins). The
 * caller's `panelClassName` keeps its padding/spacing; its WIDTH is
 * overridden inline, which is what makes one prop serve both shapes.
 *  - It never moves with its trigger, so it does NOT close on scroll/resize
 *    (a phone keyboard opening is a resize) — unlike the `portal` path.
 *  - 🪤 The backdrop closes on CLICK, never on mousedown, and the outside-
 *    mousedown listener ignores it: closing on mousedown unmounts the
 *    backdrop before the click, so the click lands on whatever is under it —
 *    a bottom-nav link. Same reason the outside test exists at all.
 *  - Focus: unchanged (stays on the trigger; Escape returns it there). As a
 *    portal the panel is no longer next in tab order, so Tab from the trigger
 *    while the sheet is open moves focus INTO the sheet.
 *  - jsdom has no `matchMedia`, so every existing test renders the desktop
 *    branch byte-for-byte; the sheet is pinned by `Dropdown.sheet.test.js`.
 * At >=768px nothing here runs: anchored or `portal` exactly as before.
 *
 * The panel content is a render-prop (or node) — it receives { close } so an
 * item that should dismiss the menu (e.g. applying a saved view) can, while
 * live-applied controls (filters) simply leave it open.
 *
 * Props:
 *  - label: trigger button content (text/node)
 *  - count: optional number — renders a "(N)" badge on the trigger when > 0
 *  - triggerClassName: classes for the trigger button (usually BTN.secondary)
 *  - align: 'left' | 'right' — which edge the panel anchors to
 *  - panelClassName: width/padding for the panel (e.g. 'w-72 p-2')
 *  - title / ariaLabel: tooltip + panel aria-label
 *  - disabled: disables the trigger
 *  - haspopup: 'menu' (default) | 'dialog'
 *  - triggerTestId: optional `data-testid` on the TRIGGER. Added 2026-08-08:
 *    the panel's contents are not in the DOM while the popover is closed, so a
 *    test that wants anything inside it has to open it first, and without a
 *    testid the only handle on the trigger is a text or `aria-haspopup` match.
 *    Purely additive — no existing caller is affected.
 *  - portal: opt-in (default false). Render the panel in a portal to
 *    document.body at `position: fixed` / `zIndex: 60` instead of anchoring it
 *    absolutely. Use it — and only use it — when an ancestor of the TRIGGER
 *    clips (`overflow` anything but `visible`). See the block above.
 *  - children: node | ({ close }) => node
 */
export default function Dropdown({
  label,
  count,
  triggerClassName,
  align = 'left',
  panelClassName = '',
  title,
  ariaLabel,
  disabled = false,
  haspopup = 'menu',
  triggerTestId,
  portal = false,
  // Opt-in (default off — every existing trigger is byte-identical): a
  // glyph-only trigger such as a ⋯ menu drops the rotating chevron, which
  // otherwise wraps under the glyph in a narrow 44px target.
  hideChevron = false,
  children,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  // Below `md` the panel is a bottom sheet (see the block above). The same
  // breakpoint + no-`matchMedia` → desktop rule as the order pages.
  const sheet = useIsMobileOrderViewport();
  // ── Portal path only: position, and dismiss on scroll/resize ──
  // Extracted 2026-10-19 to `hooks/useAnchoredPortalPanel.js` (shared with
  // `ui/SuggestInput`); the rules and their reasons are the block above.
  // `coords` stays `null` while anchored, so the anchored render below is
  // reached by exactly the code it was reached by before `portal` existed.
  const { coords, primeCoords } = useAnchoredPortalPanel({
    open, enabled: portal && !sheet, align, triggerRef, panelRef,
    onDismiss: () => setOpen(false),
  });

  // Close on outside click / Escape — same pattern as GagSavedViews, plus focus
  // return to the trigger on Escape so keyboard users aren't dropped to <body>.
  useEffect(() => {
    if (!open) return undefined;
    // 🔑 A PORTALLED panel is not a DOM descendant of rootRef, so the original
    // containment test alone would treat every click inside it as an outside
    // click and close it on the first checkbox. While anchored, the panel IS
    // inside rootRef, so the second operand is a no-op and this behaves
    // exactly as it did before `portal` existed.
    const isInside = (t) =>
      !!(rootRef.current?.contains(t) || panelRef.current?.contains(t));
    const onClickOutside = (e) => {
      // The sheet's backdrop closes on CLICK (its own handler) — see the
      // 🪤 in the sheet block above.
      if (backdropRef.current && backdropRef.current.contains(e.target)) return;
      if (!isInside(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      // Sheet only: a portalled panel is not next in tab order, so Tab from
      // the trigger moves into it rather than past it.
      if (
        sheet && e.key === 'Tab' && !e.shiftKey &&
        document.activeElement === triggerRef.current && panelRef.current
      ) {
        const first = panelRef.current.querySelector(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (first) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, sheet]);

  // A first guess so the panel never paints at 0,0 (portal path only — a
  // no-op otherwise); the hook refines it before paint. Mirrors KebabMenu.
  const openPanel = () => {
    primeCoords();
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-haspopup={haspopup}
        aria-expanded={open}
        title={title}
        data-testid={triggerTestId}
        className={triggerClassName}
      >
        {label}
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
            {count}
          </span>
        )}
        {!hideChevron && <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>}
      </button>

      {open && !portal && !sheet && (
        <div
          ref={panelRef}
          role={haspopup === 'dialog' ? 'dialog' : undefined}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-1 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 ${panelClassName}`}
          aria-label={ariaLabel}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      )}

      {/* 🔑 Same panel, same children, same `close` — only the containing block
          and the z tier differ. `zIndex` is an INLINE style, not a Tailwind
          class, exactly as KebabMenu writes it: it is the contract's `z-60`
          escape hatch and needs no arbitrary-value class compiled for it. */}
      {open && portal && !sheet && coords && createPortal(
        <div
          ref={panelRef}
          data-portal-dropdown="true"
          role={haspopup === 'dialog' ? 'dialog' : undefined}
          style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 60 }}
          className={`max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 ${panelClassName}`}
          aria-label={ariaLabel}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>,
        document.body
      )}

      {/* 📱 Below `md`: the bottom sheet. Width/max-width/max-height are
          INLINE so they override whatever width the caller's panelClassName
          asks for on desktop. */}
      {open && sheet && createPortal(
        <>
          <div
            ref={backdropRef}
            data-dropdown-sheet-backdrop="true"
            aria-hidden="true"
            className="fixed inset-0 bg-black/40"
            style={{ zIndex: SHEET_Z }}
            onClick={() => setOpen(false)}
          />
          <div
            ref={panelRef}
            data-dropdown-sheet="true"
            role={haspopup === 'dialog' ? 'dialog' : undefined}
            style={{
              position: 'fixed', left: 8, right: 8, bottom: SHEET_BOTTOM, zIndex: SHEET_Z,
              width: 'auto', maxWidth: 'none', maxHeight: '70vh',
            }}
            className={`overflow-y-auto overscroll-contain bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 ${panelClassName}`}
            aria-label={ariaLabel}
          >
            {typeof children === 'function' ? children({ close }) : children}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
